import { meghaPublicPagePath } from '../../megha/public-digital-profile/megha-public-profile-path.util';
import { MY_DIGITAL_HOME_BASE, myDigitalHomeBusinessRoute } from '../../megha/member-profile/my-digital-home-nav.util';
import { clinicBusinessBase } from '../../megha/clinic-business-dashboard/clinic-route.util';
import { hostelBusinessFeesRoute } from '../../megha/hostel-business-dashboard/hostel-route.util';
import { findPrimaryHostelStore } from './hostel-home-route.util';
import { findPrimaryHotelStore } from './hotel-home-route.util';
import { hotelBusinessHomeRoute } from '../../megha/hotel-business-dashboard/hotel-route.util';
import { isClinicStoreType, isPetClinicStoreType } from './clinic-store-types.util';
import { findPrimaryPetStore, petDashboardCommands } from './pet-home-route.util';
import {
  findPrimaryProfileBusinessStore,
  profileDashboardCommands
} from './profile-home-route.util';
import { consultDashboardRouteForProfile } from '../../megha/profile-business-dashboard/consult-profile-dashboard.util';

export interface ClinicStoreLike {
  store_type?: string | null;
  store_code?: string | null;
}

export interface HomeNavigationProfileLike {
  slug?: string | null;
  profile_category?: string | null;
  is_public?: boolean;
  source_store_code?: string | null;
}

/** First owned store that is a human clinic type (excludes pet-care / pet-clinic). */
export function findPrimaryClinicStore<T extends ClinicStoreLike>(
  stores: T[] | null | undefined
): T | null {
  if (!stores?.length) return null;
  return (
    stores.find(
      (s) =>
        isClinicStoreType(s.store_type) &&
        !isPetClinicStoreType(s.store_type) &&
        String(s.store_code || '').trim()
    ) ?? null
  );
}

export function clinicDashboardCommands(storeCode: string): string[] {
  return clinicBusinessBase(storeCode);
}

function isStudentProfileCategory(profileCategory: string | null | undefined): boolean {
  return String(profileCategory || '').trim().toLowerCase() === 'student';
}

/** Owned student profile — home even when private (owner can view `/student/{slug}`). */
function findOwnedStudentHomeProfile(
  profiles: HomeNavigationProfileLike[] | null | undefined
): HomeNavigationProfileLike | null {
  return (
    profiles?.find((p) => {
      const slug = String(p.slug || '').trim();
      return slug && isStudentProfileCategory(p.profile_category);
    }) ?? null
  );
}

/** First owned public profile (non-student categories require publish). */
function findOwnedPublicHomeProfile(
  profiles: HomeNavigationProfileLike[] | null | undefined
): HomeNavigationProfileLike | null {
  return (
    profiles?.find((p) => {
      const slug = String(p.slug || '').trim();
      return slug && p.is_public !== false;
    }) ?? null
  );
}

const MY_DIGITAL_HOME_DEEP_LINK_QUERY_KEYS = [
  'onboarding',
  'offerStore',
  'storeCode',
  'store',
  'category'
] as const;

function hasMyDigitalHomeDeepLinkQuery(queryString: string | undefined): boolean {
  if (!queryString?.trim()) {
    return false;
  }
  try {
    const params = new URLSearchParams(queryString.split('#')[0]);
    return MY_DIGITAL_HOME_DEEP_LINK_QUERY_KEYS.some((key) => {
      const value = params.get(key);
      return value != null && value.trim() !== '';
    });
  } catch {
    return false;
  }
}

/** True when return URL is generic marketing home, not a deep link. */
export function isGenericPostLoginReturnUrl(returnUrl: string | null | undefined): boolean {
  const raw = (returnUrl || '').trim();
  if (!raw) return true;
  const qIndex = raw.indexOf('?');
  const path = (qIndex >= 0 ? raw.slice(0, qIndex) : raw).split('#')[0].replace(/\/+$/, '') || '/';
  const query = qIndex >= 0 ? raw.slice(qIndex + 1) : '';
  if (path === MY_DIGITAL_HOME_BASE && hasMyDigitalHomeDeepLinkQuery(query)) {
    return false;
  }
  return path === '/' || path === '/home' || path === MY_DIGITAL_HOME_BASE;
}

/**
 * Logged-in member home: clinic dashboard, My Business, student profile page
 * (public or private), other public profile pages, or My Digital Home.
 * Empty accounts (no stores / profiles) open create-public-profile onboarding.
 */
export function resolveAuthenticatedHomeNavigation(
  stores: ClinicStoreLike[] | null | undefined,
  profiles: HomeNavigationProfileLike[] | null | undefined
): string | string[] | null {
  const hostel = findPrimaryHostelStore(stores);
  if (hostel?.store_code) {
    return hostelBusinessFeesRoute(String(hostel.store_code));
  }

  const hotel = findPrimaryHotelStore(stores);
  if (hotel?.store_code) {
    return hotelBusinessHomeRoute(String(hotel.store_code));
  }

  const pet = findPrimaryPetStore(stores);
  if (pet?.store_code) {
    return petDashboardCommands(String(pet.store_code));
  }

  const clinic = findPrimaryClinicStore(stores);
  if (clinic?.store_code) {
    return clinicDashboardCommands(String(clinic.store_code));
  }

  const profileBiz = findPrimaryProfileBusinessStore(stores);
  if (profileBiz?.store_code) {
    return profileDashboardCommands(String(profileBiz.store_code), profileBiz.store_type);
  }

  const store = stores?.find((s) => String(s.store_code || '').trim());
  if (store?.store_code) {
    return myDigitalHomeBusinessRoute();
  }

  const studentProfile = findOwnedStudentHomeProfile(profiles);
  if (studentProfile?.slug) {
    return [meghaPublicPagePath(String(studentProfile.slug).trim(), studentProfile.profile_category)];
  }

  // Profile-only account → business-consult shell (not just the public page).
  const consultProfile = (profiles || []).find((p) => consultDashboardRouteForProfile(p));
  if (consultProfile) {
    const route = consultDashboardRouteForProfile(consultProfile);
    if (route) return route;
  }

  const profile = findOwnedPublicHomeProfile(profiles);
  if (profile?.slug) {
    return [meghaPublicPagePath(String(profile.slug).trim(), profile.profile_category)];
  }

  // New / empty account → create public profile (Business vs Professionals).
  return `${MY_DIGITAL_HOME_BASE}?onboarding=profile`;
}

/**
 * After sign-in: dashboard or public profile unless returnUrl is a specific path.
 */
export function resolvePostLoginNavigation(
  stores: ClinicStoreLike[] | null | undefined,
  returnUrl?: string | null,
  profiles?: HomeNavigationProfileLike[] | null
): string | string[] {
  if (!isGenericPostLoginReturnUrl(returnUrl)) {
    const url = (returnUrl || '/').trim();
    return url.startsWith('/') ? url : `/${url}`;
  }
  const home = resolveAuthenticatedHomeNavigation(stores, profiles);
  return home ?? MY_DIGITAL_HOME_BASE;
}
